/**
 * AppSync Service Implementation
 *
 * @fileoverview Production-ready service for AWS AppSync GraphQL API interactions.
 * Implements AWS best practices for authentication, token management, subscription lifecycle, and error handling.
 *
 * @see {@link ./README.md|Developer Documentation} - Human-readable documentation with examples
 * @see {@link ./LLM_DOCUMENTATION.md|LLM Documentation} - LLM-optimized documentation with TypeScript types
 * @see {@link ../../../../docs/APPSYNC_SERVICE.md|Service Index} - Service overview and quick links
 * @see {@link ../../../../llms.txt|Documentation Index} - Repository-wide documentation index
 *
 * @module services/appsync/service
 */

import { fetchAuthSession } from "aws-amplify/auth";
import { getSelectedCustomerId } from "../../hooks/useCustomer";
import { appsyncClient } from "./apollo-client";
import { APPEND_CHAT_EVENT, CREATE_CHAT_THREAD } from "./mutations";
import {
  GET_CHAT_EVENTS_BY_THREAD,
  GET_CHAT_THREAD,
  GET_MY_MESSAGES_BY_CONTEXT,
  GET_MY_RUNS,
  GET_PULL_STATUSES,
  GET_VIEW_TRANSFORM,
} from "./queries";
import { calculateBackoffDelay, isRecoverableSubscriptionError } from "./subscription-manager";
import {
  ON_CHAT_EVENT,
  ON_NEW_MESSAGE,
  ON_NEW_RUN,
  ON_PULL_STATUS_UPDATE,
  ON_VIEW_TRANSFORM_UPDATE_FILTERED,
} from "./subscriptions";
import type {
  AppendChatEventInput,
  ChatEvent,
  ChatEventConnection,
  ChatMessage,
  ChatMessageConnection,
  ChatThread,
  CreateChatThreadInput,
  DataPullStatus,
  DataPullStatusConnection,
  GetChatEventsByThreadInput,
  GetChatThreadInput,
  GetMyMessagesByContextInput,
  GetMyRunsInput,
  GetPullStatusesInput,
  OnChatEventInput,
  OnNewMessageInput,
  OnNewRunInput,
  OnPullStatusUpdateInput,
  OnViewTransformUpdateInput,
  Run,
  RunConnection,
  ViewTransform,
} from "./types";

// Interface for AppSync subscription response
export interface AppSyncSubscriptionResponse {
  subscribe: (handlers: {
    next: (data: {
      data?: {
        onChatEvent?: ChatEvent;
        onNewMessage?: ChatMessage;
        onNewRun?: Run;
        onViewTransformUpdate?: ViewTransform;
      };
    }) => void;
    error: (error: Error) => void;
  }) => { unsubscribe: () => void };
}

// Subscription state management
interface SubscriptionState {
  isActive: boolean;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  reconnectTimer?: ReturnType<typeof setTimeout>;
}

/**
 * Get organizationId based on user role
 * - For ROOT users: use selected customer from localStorage
 * - For non-ROOT users: use custom:organization from id token
 */
const getOrganizationId = (idToken: string): string => {
  try {
    const payload = JSON.parse(atob(idToken.split(".")[1]));
    const role = payload["custom:role"];

    if (role === "ROOT") {
      // ROOT users can select which customer to view
      return getSelectedCustomerId();
    } else {
      // Non-ROOT users use their organization from the token
      const organizationId = payload["custom:organization"];
      if (!organizationId) {
        throw new Error("Organization ID not found in token for non-ROOT user");
      }
      return organizationId;
    }
  } catch (error) {
    console.error("Error getting organization ID:", error);
    throw new Error("Failed to determine organization ID");
  }
};

/**
 * AppSync Service
 *
 * Production-ready service for AWS AppSync GraphQL API interactions.
 * Implements AWS best practices for authentication, token management, subscription lifecycle, and error handling.
 *
 * @see {@link https://docs.aws.amazon.com/appsync/|AWS AppSync Documentation}
 * @see {@link ./README.md|Developer Documentation}
 * @see {@link ./LLM_DOCUMENTATION.md|LLM-Optimized Documentation}
 *
 * Key Features:
 * - Automatic token refresh on expiration
 * - Exponential backoff for reconnection attempts
 * - Subscription lifecycle management
 * - Cost-optimized error handling
 * - AWS best practices compliance
 */
export class AppSyncService {
  private static instance: AppSyncService;

  private constructor() {}

  private async getAuthContext(forceRefresh = false): Promise<{
    idTokenString: string;
    organizationId: string;
  }> {
    const session = await fetchAuthSession({ forceRefresh });
    const idToken = session.tokens?.idToken;

    if (!idToken) {
      throw new Error("No authentication token available");
    }

    const idTokenString = idToken.toString();
    const organizationId = getOrganizationId(idTokenString);

    return { idTokenString, organizationId };
  }

  /**
   * Get the singleton instance of AppSyncService
   * @returns AppSyncService instance
   */
  public static getInstance(): AppSyncService {
    if (!AppSyncService.instance) {
      AppSyncService.instance = new AppSyncService();
    }
    return AppSyncService.instance;
  }

  /**
   * Get messages by context
   *
   * Fetches messages for a specific organization and context with pagination support.
   * Uses ID token to ensure custom claims (custom:role, custom:organization) are available for authorization.
   *
   * @param input - Query input parameters
   * @param input.organizationId - Organization ID (required)
   * @param input.contextId - Context ID (required)
   * @param input.limit - Maximum number of items to return (optional)
   * @param input.nextToken - Pagination token for next page (optional)
   * @returns Promise resolving to ChatMessageConnection with items and nextToken
   * @throws Error if token unavailable or request fails
   *
   * @example
   * ```typescript
   * const messages = await appSyncService.getMyMessagesByContext({
   *   organizationId: 'org-123',
   *   contextId: 'ctx-456',
   *   limit: 20,
   * });
   * ```
   *
   * @see {@link ./README.md|Documentation} for usage examples
   * @see {@link ./LLM_DOCUMENTATION.md|LLM Documentation} for technical details
   *
   * NOTE: Uses authToken option with ID token to ensure custom claims are available.
   * Amplify v6's generateClient() with authMode: "userPool" sends the access token
   * instead of the ID token, which doesn't contain custom attributes.
   */
  async getMyMessagesByContext(input: GetMyMessagesByContextInput): Promise<ChatMessageConnection> {
    try {
      const { idTokenString, organizationId } = await this.getAuthContext();

      // Override organizationId in input with the determined value
      const queryInput = {
        ...input,
        organizationId,
      };

      // Use authToken option to explicitly send ID token (contains custom claims)
      // This still uses Amplify's client infrastructure and token refresh
      const result = await appsyncClient.graphql({
        query: GET_MY_MESSAGES_BY_CONTEXT,
        variables: queryInput,
        authMode: "userPool",
        authToken: idTokenString, // Explicitly use ID token instead of access token
      });

      if ("data" in result && result.data) {
        return result.data.getMyMessagesByContext;
      }
      throw new Error("No data received from AppSync");
    } catch (error) {
      console.error("Error fetching messages by context:", error);
      throw error;
    }
  }

  /**
   * Get my runs
   *
   * Fetches runs for a specific organization with pagination support.
   * Uses ID token to ensure custom claims (custom:role, custom:organization) are available for authorization.
   *
   * @param input - Query input parameters
   * @param input.organizationId - Organization ID (required)
   * @param input.limit - Maximum number of items to return (optional)
   * @param input.nextToken - Pagination token for next page (optional)
   * @returns Promise resolving to RunConnection with items and nextToken
   * @throws Error if token unavailable or request fails
   *
   * @example
   * ```typescript
   * const runs = await appSyncService.getMyRuns({
   *   organizationId: 'org-123',
   *   limit: 20,
   * });
   * ```
   *
   * @see {@link ./README.md|Documentation} for usage examples
   * @see {@link ./LLM_DOCUMENTATION.md|LLM Documentation} for technical details
   *
   * NOTE: Uses authToken option with ID token to ensure custom claims are available.
   * Amplify v6's generateClient() with authMode: "userPool" sends the access token
   * instead of the ID token, which doesn't contain custom attributes.
   */
  async getMyRuns(input: GetMyRunsInput): Promise<RunConnection> {
    try {
      const { idTokenString, organizationId } = await this.getAuthContext();

      // Override organizationId in input with the determined value
      const queryInput = {
        ...input,
        organizationId,
      };

      // Use authToken option to explicitly send ID token (contains custom claims)
      // This still uses Amplify's client infrastructure and token refresh
      const result = await appsyncClient.graphql({
        query: GET_MY_RUNS,
        variables: queryInput,
        authMode: "userPool",
        authToken: idTokenString, // Explicitly use ID token instead of access token
      });

      if ("data" in result && result.data) {
        return result.data.getMyRuns;
      }
      throw new Error("No data received from AppSync");
    } catch (error) {
      console.error("Error fetching my runs:", error);
      // Log more details about the error
      if (error && typeof error === "object" && "errors" in error) {
        console.error("AppSync errors:", JSON.stringify(error.errors, null, 2));
      }
      throw error;
    }
  }

  /**
   * One-shot read of a view transform by ID. Used as a safety net when the
   * subscription is opened lazily (only after a mutation returns IN_PROGRESS):
   * a COMPLETED status committed during the brief socket-open window would not
   * be delivered over the subscription, so we read once on open to catch it.
   */
  async getViewTransform(id: string): Promise<ViewTransform | null> {
    try {
      const { idTokenString, organizationId } = await this.getAuthContext();

      const result = await appsyncClient.graphql({
        query: GET_VIEW_TRANSFORM,
        variables: { organizationId, id },
        authMode: "userPool",
        authToken: idTokenString,
      });

      if ("data" in result && result.data) {
        return result.data.getViewTransform ?? null;
      }
      return null;
    } catch (error) {
      console.error("Error fetching view transform:", error);
      return null;
    }
  }

  async createChatThread(input: CreateChatThreadInput): Promise<ChatThread> {
    try {
      const { idTokenString, organizationId } = await this.getAuthContext();

      const result = await appsyncClient.graphql({
        query: CREATE_CHAT_THREAD,
        variables: {
          ...input,
          organizationId,
        },
        authMode: "userPool",
        authToken: idTokenString,
      });

      if ("data" in result && result.data) {
        return result.data.createChatThread;
      }
      throw new Error("No data received from AppSync");
    } catch (error) {
      console.error("Error creating chat thread:", error);
      throw error;
    }
  }

  async getChatThread(input: GetChatThreadInput): Promise<ChatThread | null> {
    try {
      const { idTokenString, organizationId } = await this.getAuthContext();

      const result = await appsyncClient.graphql({
        query: GET_CHAT_THREAD,
        variables: {
          ...input,
          organizationId,
        },
        authMode: "userPool",
        authToken: idTokenString,
      });

      if ("data" in result && result.data) {
        return result.data.getChatThread ?? null;
      }
      throw new Error("No data received from AppSync");
    } catch (error) {
      console.error("Error fetching chat thread:", error);
      throw error;
    }
  }

  async getChatEventsByThread(input: GetChatEventsByThreadInput): Promise<ChatEventConnection> {
    try {
      const { idTokenString, organizationId } = await this.getAuthContext();

      const result = await appsyncClient.graphql({
        query: GET_CHAT_EVENTS_BY_THREAD,
        variables: {
          ...input,
          organizationId,
        },
        authMode: "userPool",
        authToken: idTokenString,
      });

      if ("data" in result && result.data) {
        return result.data.getChatEventsByThread;
      }
      throw new Error("No data received from AppSync");
    } catch (error) {
      console.error("Error fetching chat events:", error);
      throw error;
    }
  }

  async appendChatEvent(input: AppendChatEventInput): Promise<ChatEvent> {
    try {
      const { idTokenString, organizationId } = await this.getAuthContext();

      const result = await appsyncClient.graphql({
        query: APPEND_CHAT_EVENT,
        variables: {
          ...input,
          organizationId,
        },
        authMode: "userPool",
        authToken: idTokenString,
      });

      if ("data" in result && result.data) {
        return result.data.appendChatEvent;
      }
      throw new Error("No data received from AppSync");
    } catch (error) {
      console.error("Error appending chat event:", error);
      throw error;
    }
  }

  /**
   * Subscribe to new messages with automatic token refresh and reconnection
   *
   * Creates a subscription to new messages for a specific organization and context.
   * Automatically handles token expiration, reconnection, and error recovery.
   *
   * @param input - Subscription input parameters
   * @param input.organizationId - Organization ID (required)
   * @param input.contextId - Context ID (required)
   * @param onMessage - Callback function called when a new message is received
   * @param onError - Optional callback function called on non-recoverable errors
   * @param subscriptionState - Optional subscription state for internal reconnection management
   * @returns Promise resolving to subscription object with unsubscribe method
   *
   * @example
   * ```typescript
   * const subscription = await appSyncService.subscribeToNewMessages(
   *   { organizationId: 'org-123', contextId: 'ctx-456' },
   *   (message) => console.log('New message:', message),
   *   (error) => console.error('Subscription error:', error)
   * );
   *
   * // Cleanup when done
   * subscription.unsubscribe();
   * ```
   *
   * @see {@link ./README.md|Documentation} for usage examples
   * @see {@link ./LLM_DOCUMENTATION.md|LLM Documentation} for technical details
   *
   * Features:
   * - Automatic token refresh on expiration
   * - Exponential backoff for reconnection (1s → 2s → 4s → 8s → 16s → 30s max)
   * - Maximum 5 reconnection attempts
   * - Proper cleanup and resource management
   * - Cost-optimized error handling
   *
   * NOTE: Uses authToken option with ID token to ensure custom claims are available.
   * Follows AWS AppSync best practices for subscription management.
   */
  async subscribeToNewMessages(
    input: OnNewMessageInput,
    onMessage: (message: ChatMessage) => void,
    onError?: (error: Error) => void,
    subscriptionState?: SubscriptionState
  ): Promise<{ unsubscribe: () => void }> {
    const state: SubscriptionState = subscriptionState || {
      isActive: true,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5, // AWS best practice: limit reconnection attempts
    };

    // Prevent reconnection if max attempts reached or subscription is inactive
    if (!state.isActive || state.reconnectAttempts >= state.maxReconnectAttempts) {
      if (state.reconnectAttempts >= state.maxReconnectAttempts) {
        const error = new Error(
          `Subscription reconnection failed after ${state.maxReconnectAttempts} attempts`
        );
        console.error("Max reconnection attempts reached:", error);
        if (onError) {
          onError(error);
        }
      }
      return { unsubscribe: () => {} };
    }

    try {
      const { idTokenString, organizationId } = await this.getAuthContext(
        state.reconnectAttempts > 0
      );

      // Override organizationId in input with the determined value
      const subscriptionInput = {
        ...input,
        organizationId,
      };

      // Reset reconnect attempts on successful connection
      state.reconnectAttempts = 0;

      const subscription = (
        appsyncClient.graphql({
          query: ON_NEW_MESSAGE,
          variables: subscriptionInput,
          authMode: "userPool",
          authToken: idTokenString, // Explicitly use ID token instead of access token
        }) as AppSyncSubscriptionResponse
      ).subscribe({
        next: (data: { data?: { onNewMessage?: ChatMessage } }) => {
          // Reset reconnect attempts on successful message delivery
          state.reconnectAttempts = 0;
          if (data.data?.onNewMessage) {
            onMessage(data.data.onNewMessage);
          }
        },
        error: (error: Error) => {
          console.error("Error in new message subscription:", error);

          // Recoverable (token/network): reconnect with backoff, do not surface to user
          if (isRecoverableSubscriptionError(error) && state.isActive) {
            state.reconnectAttempts++;

            // Calculate exponential backoff delay (AWS best practice)
            const backoffDelay = calculateBackoffDelay(
              state.reconnectAttempts - 1,
              1000, // Base delay: 1 second
              30000 // Max delay: 30 seconds
            );

            // Clear any existing reconnect timer
            if (state.reconnectTimer) {
              clearTimeout(state.reconnectTimer);
            }

            // Schedule reconnection with exponential backoff
            state.reconnectTimer = setTimeout(() => {
              if (state.isActive) {
                // Reuse the same input (organizationId will be recalculated)
                this.subscribeToNewMessages(input, onMessage, onError, state).catch(
                  (reconnectError) => {
                    console.error("Failed to reconnect subscription:", reconnectError);
                    if (onError) {
                      onError(reconnectError as Error);
                    }
                  }
                );
              }
            }, backoffDelay);

            return;
          }

          // Non-recoverable: call the error handler
          if (onError) {
            onError(error);
          }
        },
      });

      return {
        unsubscribe: () => {
          state.isActive = false;
          if (state.reconnectTimer) {
            clearTimeout(state.reconnectTimer);
          }
          subscription.unsubscribe();
        },
      };
    } catch (error) {
      console.error("Error setting up message subscription:", error);

      // Recoverable (token/network): attempt reconnection with backoff
      if (isRecoverableSubscriptionError(error) && state.isActive) {
        state.reconnectAttempts++;
        const backoffDelay = calculateBackoffDelay(state.reconnectAttempts - 1);

        state.reconnectTimer = setTimeout(() => {
          if (state.isActive) {
            // Reuse the same input (organizationId will be recalculated)
            this.subscribeToNewMessages(input, onMessage, onError, state).catch(
              (reconnectError) => {
                console.error("Failed to reconnect subscription:", reconnectError);
                if (onError) {
                  onError(reconnectError as Error);
                }
              }
            );
          }
        }, backoffDelay);

        // Return a placeholder unsubscribe that will be replaced on reconnection
        return {
          unsubscribe: () => {
            state.isActive = false;
            if (state.reconnectTimer) {
              clearTimeout(state.reconnectTimer);
            }
          },
        };
      }

      throw error;
    }
  }

  /**
   * Subscribe to new runs with automatic token refresh and reconnection
   *
   * Creates a subscription to new runs for a specific organization.
   * Automatically handles token expiration, reconnection, and error recovery.
   *
   * @param input - Subscription input parameters
   * @param input.organizationId - Organization ID (required)
   * @param onRun - Callback function called when a new run is received
   * @param onError - Optional callback function called on non-recoverable errors
   * @param subscriptionState - Optional subscription state for internal reconnection management
   * @returns Promise resolving to subscription object with unsubscribe method
   *
   * @example
   * ```typescript
   * const subscription = await appSyncService.subscribeToNewRuns(
   *   { organizationId: 'org-123' },
   *   (run) => console.log('New run:', run),
   *   (error) => console.error('Subscription error:', error)
   * );
   *
   * // Cleanup when done
   * subscription.unsubscribe();
   * ```
   *
   * @see {@link ./README.md|Documentation} for usage examples
   * @see {@link ./LLM_DOCUMENTATION.md|LLM Documentation} for technical details
   *
   * Features:
   * - Automatic token refresh on expiration
   * - Exponential backoff for reconnection (1s → 2s → 4s → 8s → 16s → 30s max)
   * - Maximum 5 reconnection attempts
   * - Proper cleanup and resource management
   * - Cost-optimized error handling
   *
   * NOTE: Uses authToken option with ID token to ensure custom claims are available.
   * Follows AWS AppSync best practices for subscription management.
   */
  async subscribeToNewRuns(
    input: OnNewRunInput,
    onRun: (run: Run) => void,
    onError?: (error: Error) => void,
    subscriptionState?: SubscriptionState
  ): Promise<{ unsubscribe: () => void }> {
    const state: SubscriptionState = subscriptionState || {
      isActive: true,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5, // AWS best practice: limit reconnection attempts
    };

    // Prevent reconnection if max attempts reached or subscription is inactive
    if (!state.isActive || state.reconnectAttempts >= state.maxReconnectAttempts) {
      if (state.reconnectAttempts >= state.maxReconnectAttempts) {
        const error = new Error(
          `Subscription reconnection failed after ${state.maxReconnectAttempts} attempts`
        );
        console.error("Max reconnection attempts reached:", error);
        if (onError) {
          onError(error);
        }
      }
      return { unsubscribe: () => {} };
    }

    try {
      const { idTokenString, organizationId } = await this.getAuthContext(
        state.reconnectAttempts > 0
      );

      // Override organizationId in input with the determined value
      const subscriptionInput = {
        ...input,
        organizationId,
      };

      // Reset reconnect attempts on successful connection
      state.reconnectAttempts = 0;

      const subscription = (
        appsyncClient.graphql({
          query: ON_NEW_RUN,
          variables: subscriptionInput,
          authMode: "userPool",
          authToken: idTokenString, // Explicitly use ID token instead of access token
        }) as AppSyncSubscriptionResponse
      ).subscribe({
        next: (data: { data?: { onNewRun?: Run } }) => {
          // Reset reconnect attempts on successful message delivery
          state.reconnectAttempts = 0;
          if (data.data?.onNewRun) {
            onRun(data.data.onNewRun);
          }
        },
        error: (error: Error) => {
          console.error("Error in new run subscription:", error);

          // Recoverable (token/network): reconnect with backoff, do not surface to user
          if (isRecoverableSubscriptionError(error) && state.isActive) {
            state.reconnectAttempts++;

            // Calculate exponential backoff delay (AWS best practice)
            const backoffDelay = calculateBackoffDelay(
              state.reconnectAttempts - 1,
              1000, // Base delay: 1 second
              30000 // Max delay: 30 seconds
            );

            // Clear any existing reconnect timer
            if (state.reconnectTimer) {
              clearTimeout(state.reconnectTimer);
            }

            // Schedule reconnection with exponential backoff
            state.reconnectTimer = setTimeout(() => {
              if (state.isActive) {
                // Reuse the same input (organizationId will be recalculated)
                this.subscribeToNewRuns(input, onRun, onError, state).catch((reconnectError) => {
                  console.error("Failed to reconnect subscription:", reconnectError);
                  if (onError) {
                    onError(reconnectError as Error);
                  }
                });
              }
            }, backoffDelay);

            return;
          }

          // Non-recoverable: call the error handler
          if (onError) {
            onError(error);
          }
        },
      });

      return {
        unsubscribe: () => {
          state.isActive = false;
          if (state.reconnectTimer) {
            clearTimeout(state.reconnectTimer);
          }
          subscription.unsubscribe();
        },
      };
    } catch (error) {
      console.error("Error setting up run subscription:", error);

      // Recoverable (token/network): attempt reconnection with backoff
      if (isRecoverableSubscriptionError(error) && state.isActive) {
        state.reconnectAttempts++;
        const backoffDelay = calculateBackoffDelay(state.reconnectAttempts - 1);

        state.reconnectTimer = setTimeout(() => {
          if (state.isActive) {
            // Reuse the same input (organizationId will be recalculated)
            this.subscribeToNewRuns(input, onRun, onError, state).catch((reconnectError) => {
              console.error("Failed to reconnect subscription:", reconnectError);
              if (onError) {
                onError(reconnectError as Error);
              }
            });
          }
        }, backoffDelay);

        // Return a placeholder unsubscribe that will be replaced on reconnection
        return {
          unsubscribe: () => {
            state.isActive = false;
            if (state.reconnectTimer) {
              clearTimeout(state.reconnectTimer);
            }
          },
        };
      }

      throw error;
    }
  }

  /**
   * Subscribe to view transform updates with automatic token refresh and reconnection
   *
   * Creates a subscription to view transform updates for a specific organization.
   * Automatically handles token expiration, reconnection, and error recovery.
   *
   * @param input - Subscription input parameters
   * @param input.organizationId - Organization ID (required)
   * @param onUpdate - Callback function called when a view transform is updated
   * @param onError - Optional callback function called on non-recoverable errors
   * @param subscriptionState - Optional subscription state for internal reconnection management
   * @returns Promise resolving to subscription object with unsubscribe method
   *
   * @example
   * ```typescript
   * const subscription = await appSyncService.subscribeToViewTransformUpdates(
   *   { organizationId: 'org-123' },
   *   (viewTransform) => console.log('View transform updated:', viewTransform),
   *   (error) => console.error('Subscription error:', error)
   * );
   *
   * // Cleanup when done
   * subscription.unsubscribe();
   * ```
   *
   * @see {@link ./README.md|Documentation} for usage examples
   * @see {@link ./LLM_DOCUMENTATION.md|LLM Documentation} for technical details
   *
   * Features:
   * - Automatic token refresh on expiration
   * - Exponential backoff for reconnection (1s → 2s → 4s → 8s → 16s → 30s max)
   * - Maximum 5 reconnection attempts
   * - Proper cleanup and resource management
   * - Cost-optimized error handling
   *
   * NOTE: Uses authToken option with ID token to ensure custom claims are available.
   * Follows AWS AppSync best practices for subscription management.
   */
  async subscribeToViewTransformUpdates(
    input: OnViewTransformUpdateInput,
    onUpdate: (viewTransform: ViewTransform) => void,
    onError?: (error: Error) => void,
    subscriptionState?: SubscriptionState
  ): Promise<{ unsubscribe: () => void }> {
    const state: SubscriptionState = subscriptionState || {
      isActive: true,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5, // AWS best practice: limit reconnection attempts
    };

    // Prevent reconnection if max attempts reached or subscription is inactive
    if (!state.isActive || state.reconnectAttempts >= state.maxReconnectAttempts) {
      if (state.reconnectAttempts >= state.maxReconnectAttempts) {
        const error = new Error(
          `Subscription reconnection failed after ${state.maxReconnectAttempts} attempts`
        );
        console.error("Max reconnection attempts reached:", error);
        if (onError) {
          onError(error);
        }
      }
      return { unsubscribe: () => {} };
    }

    try {
      const { idTokenString, organizationId } = await this.getAuthContext(
        state.reconnectAttempts > 0
      );

      // Override organizationId in input with the determined value
      const subscriptionInput = {
        ...input,
        organizationId,
      };

      // Reset reconnect attempts on successful connection
      state.reconnectAttempts = 0;

      // Always use the filtered subscription for strict isolation
      // All required parameters (transformId, runSettings, transformationSettings) must be provided
      if (
        !subscriptionInput.transformId ||
        !subscriptionInput.transformationSettings ||
        !subscriptionInput.runSettings
      ) {
        throw new Error(
          "transformId, transformationSettings, and runSettings are required for view transform subscription"
        );
      }

      const subscription = (
        appsyncClient.graphql({
          query: ON_VIEW_TRANSFORM_UPDATE_FILTERED, // ✅ FILTERED subscription
          variables: subscriptionInput,
          authMode: "userPool",
          authToken: idTokenString, // Explicitly use ID token instead of access token
        }) as {
          subscribe: (handlers: {
            next: (data: { data?: { onViewTransformUpdateFiltered?: ViewTransform } }) => void;
            error: (error: Error) => void;
          }) => { unsubscribe: () => void };
        }
      ).subscribe({
        next: (data: { data?: { onViewTransformUpdateFiltered?: ViewTransform } }) => {
          const viewTransform = data.data?.onViewTransformUpdateFiltered;
          // Reset reconnect attempts on successful message delivery
          state.reconnectAttempts = 0;
          if (viewTransform) {
            onUpdate(viewTransform);
          }
        },
        error: (error: Error) => {
          console.error("Error in view transform subscription:", error);

          // Recoverable (token/network): reconnect with backoff, do not surface to user
          if (isRecoverableSubscriptionError(error) && state.isActive) {
            state.reconnectAttempts++;

            // Calculate exponential backoff delay (AWS best practice)
            const backoffDelay = calculateBackoffDelay(
              state.reconnectAttempts - 1,
              1000, // Base delay: 1 second
              30000 // Max delay: 30 seconds
            );

            // Clear any existing reconnect timer
            if (state.reconnectTimer) {
              clearTimeout(state.reconnectTimer);
            }

            // Schedule reconnection with exponential backoff
            state.reconnectTimer = setTimeout(() => {
              if (state.isActive) {
                // Reuse the same input (organizationId will be recalculated)
                this.subscribeToViewTransformUpdates(input, onUpdate, onError, state).catch(
                  (reconnectError) => {
                    console.error("Failed to reconnect subscription:", reconnectError);
                    if (onError) {
                      onError(reconnectError as Error);
                    }
                  }
                );
              }
            }, backoffDelay);

            return;
          }

          // Non-recoverable: call the error handler
          if (onError) {
            onError(error);
          }
        },
      });

      return {
        unsubscribe: () => {
          state.isActive = false;
          if (state.reconnectTimer) {
            clearTimeout(state.reconnectTimer);
          }
          subscription.unsubscribe();
        },
      };
    } catch (error) {
      console.error("Error setting up view transform subscription:", error);

      // Recoverable (token/network): attempt reconnection with backoff
      if (isRecoverableSubscriptionError(error) && state.isActive) {
        state.reconnectAttempts++;
        const backoffDelay = calculateBackoffDelay(state.reconnectAttempts - 1);

        state.reconnectTimer = setTimeout(() => {
          if (state.isActive) {
            // Reuse the same input (organizationId will be recalculated)
            this.subscribeToViewTransformUpdates(input, onUpdate, onError, state).catch(
              (reconnectError) => {
                console.error("Failed to reconnect subscription:", reconnectError);
                if (onError) {
                  onError(reconnectError as Error);
                }
              }
            );
          }
        }, backoffDelay);

        // Return a placeholder unsubscribe that will be replaced on reconnection
        return {
          unsubscribe: () => {
            state.isActive = false;
            if (state.reconnectTimer) {
              clearTimeout(state.reconnectTimer);
            }
          },
        };
      }

      throw error;
    }
  }

  /**
   * Get pull statuses by organization ID
   */
  async getPullStatuses(input: GetPullStatusesInput): Promise<DataPullStatusConnection> {
    try {
      const { idTokenString, organizationId } = await this.getAuthContext();

      const allItems: DataPullStatus[] = [];
      let nextToken: string | undefined = undefined;

      do {
        const queryInput: GetPullStatusesInput = {
          ...input,
          organizationId,
          nextToken: nextToken ?? null,
        };

        const result = (await appsyncClient.graphql({
          query: GET_PULL_STATUSES,
          variables: queryInput,
          authMode: "userPool",
          authToken: idTokenString,
        })) as {
          data?: {
            getPullStatusesByOrganizationId?: DataPullStatusConnection | null;
          };
        };

        if ("data" in result && result.data) {
          const page: DataPullStatusConnection | null | undefined =
            result.data.getPullStatusesByOrganizationId;
          if (!page) {
            break;
          }
          if (page.items) {
            allItems.push(...page.items);
          }
          nextToken = page.nextToken ?? undefined;
        } else {
          break;
        }
      } while (nextToken);

      return { items: allItems, nextToken: null };
    } catch (error) {
      console.error("Error fetching pull statuses:", error);
      throw error;
    }
  }

  /**
   * Subscribe to pull status updates
   */
  async subscribeToPullStatusUpdates(
    input: OnPullStatusUpdateInput,
    onUpdate: (status: DataPullStatus) => void,
    onError?: (error: Error) => void,
    subscriptionState?: SubscriptionState
  ): Promise<{ unsubscribe: () => void }> {
    const state: SubscriptionState = subscriptionState || {
      isActive: true,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5,
    };

    if (!state.isActive || state.reconnectAttempts >= state.maxReconnectAttempts) {
      if (state.reconnectAttempts >= state.maxReconnectAttempts) {
        const error = new Error(
          `Subscription reconnection failed after ${state.maxReconnectAttempts} attempts`
        );
        console.error("Max reconnection attempts reached:", error);
        if (onError) {
          onError(error);
        }
      }
      return { unsubscribe: () => {} };
    }

    try {
      const { idTokenString } = await this.getAuthContext(state.reconnectAttempts > 0);

      state.reconnectAttempts = 0;

      const subscription = (
        appsyncClient.graphql({
          query: ON_PULL_STATUS_UPDATE,
          variables: {},
          authMode: "userPool",
          authToken: idTokenString,
        }) as {
          subscribe: (handlers: {
            next: (data: { data?: { onPullStatusUpdate?: DataPullStatus } }) => void;
            error: (error: Error) => void;
          }) => { unsubscribe: () => void };
        }
      ).subscribe({
        next: (data: { data?: { onPullStatusUpdate?: DataPullStatus } }) => {
          state.reconnectAttempts = 0;
          if (data.data?.onPullStatusUpdate) {
            onUpdate(data.data.onPullStatusUpdate);
          }
        },
        error: (error: Error) => {
          console.error("Error in pull status subscription:", error);

          if (isRecoverableSubscriptionError(error) && state.isActive) {
            state.reconnectAttempts++;
            const backoffDelay = calculateBackoffDelay(
              state.reconnectAttempts - 1,
              1000,
              30000
            );

            if (state.reconnectTimer) {
              clearTimeout(state.reconnectTimer);
            }

            state.reconnectTimer = setTimeout(() => {
              if (state.isActive) {
                this.subscribeToPullStatusUpdates(input, onUpdate, onError, state).catch(
                  (reconnectError) => {
                    console.error("Failed to reconnect subscription:", reconnectError);
                    if (onError) {
                      onError(reconnectError as Error);
                    }
                  }
                );
              }
            }, backoffDelay);

            return;
          }

          if (onError) {
            onError(error);
          }
        },
      });

      return {
        unsubscribe: () => {
          state.isActive = false;
          if (state.reconnectTimer) {
            clearTimeout(state.reconnectTimer);
          }
          subscription.unsubscribe();
        },
      };
    } catch (error) {
      console.error("Error setting up pull status subscription:", error);

      if (isRecoverableSubscriptionError(error) && state.isActive) {
        state.reconnectAttempts++;
        const backoffDelay = calculateBackoffDelay(state.reconnectAttempts - 1);

        state.reconnectTimer = setTimeout(() => {
          if (state.isActive) {
            this.subscribeToPullStatusUpdates(input, onUpdate, onError, state).catch(
              (reconnectError) => {
                console.error("Failed to reconnect subscription:", reconnectError);
                if (onError) {
                  onError(reconnectError as Error);
                }
              }
            );
          }
        }, backoffDelay);

        return {
          unsubscribe: () => {
            state.isActive = false;
            if (state.reconnectTimer) {
              clearTimeout(state.reconnectTimer);
            }
          },
        };
      }

      throw error;
    }
  }

  async subscribeToChatEvents(
    input: OnChatEventInput,
    onEvent: (event: ChatEvent) => void,
    onError?: (error: Error) => void,
    subscriptionState?: SubscriptionState
  ): Promise<{ unsubscribe: () => void }> {
    const state: SubscriptionState = subscriptionState || {
      isActive: true,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5,
    };

    if (!state.isActive || state.reconnectAttempts >= state.maxReconnectAttempts) {
      if (state.reconnectAttempts >= state.maxReconnectAttempts) {
        const error = new Error(
          `Subscription reconnection failed after ${state.maxReconnectAttempts} attempts`
        );
        console.error("Max reconnection attempts reached:", error);
        if (onError) {
          onError(error);
        }
      }
      return { unsubscribe: () => {} };
    }

    try {
      const { idTokenString, organizationId } = await this.getAuthContext(
        state.reconnectAttempts > 0
      );

      const subscription = (
        appsyncClient.graphql({
          query: ON_CHAT_EVENT,
          variables: {
            ...input,
            organizationId,
          },
          authMode: "userPool",
          authToken: idTokenString,
        }) as AppSyncSubscriptionResponse
      ).subscribe({
        next: (data: { data?: { onChatEvent?: ChatEvent } }) => {
          state.reconnectAttempts = 0;
          if (data.data?.onChatEvent) {
            onEvent(data.data.onChatEvent);
          }
        },
        error: (error: Error) => {
          console.error("Error in chat event subscription:", error);

          if (isRecoverableSubscriptionError(error) && state.isActive) {
            state.reconnectAttempts++;

            const backoffDelay = calculateBackoffDelay(
              state.reconnectAttempts - 1,
              1000,
              30000
            );

            if (state.reconnectTimer) {
              clearTimeout(state.reconnectTimer);
            }

            state.reconnectTimer = setTimeout(() => {
              if (state.isActive) {
                this.subscribeToChatEvents(input, onEvent, onError, state).catch(
                  (reconnectError) => {
                    console.error("Failed to reconnect chat event subscription:", reconnectError);
                    if (onError) {
                      onError(reconnectError as Error);
                    }
                  }
                );
              }
            }, backoffDelay);

            return;
          }

          if (onError) {
            onError(error);
          }
        },
      });

      return {
        unsubscribe: () => {
          state.isActive = false;
          if (state.reconnectTimer) {
            clearTimeout(state.reconnectTimer);
          }
          subscription.unsubscribe();
        },
      };
    } catch (error) {
      console.error("Error setting up chat event subscription:", error);

      if (isRecoverableSubscriptionError(error) && state.isActive) {
        state.reconnectAttempts++;
        const backoffDelay = calculateBackoffDelay(state.reconnectAttempts - 1);

        state.reconnectTimer = setTimeout(() => {
          if (state.isActive) {
            this.subscribeToChatEvents(input, onEvent, onError, state).catch(
              (reconnectError) => {
                console.error("Failed to reconnect chat event subscription:", reconnectError);
                if (onError) {
                  onError(reconnectError as Error);
                }
              }
            );
          }
        }, backoffDelay);

        return {
          unsubscribe: () => {
            state.isActive = false;
            if (state.reconnectTimer) {
              clearTimeout(state.reconnectTimer);
            }
          },
        };
      }

      throw error;
    }
  }
}

export const appSyncService = AppSyncService.getInstance();
